import {
  type CollectionItem,
  findCardTemplateBlock,
  getEnabledTextFields,
  resolveCardConfigFromTemplate,
} from '@/lib/blocks/collection/blocks';
import {
  isDataWidgetBlock,
  resolveDataWidgetProps,
} from '@/lib/blocks/collection/data-widget/blocks';
import { readCollectionItems } from '@/lib/blocks/collection/item-utils';
import { buildBlockTree } from '@/lib/blocks/tree';
import { getMainSections, getSortedSections } from '@/lib/page-utils';
import { normalizeRichTextContent, richTextToPlainText } from '@/lib/rich-text';
import { BLOCK_TYPE, type PageBlock } from '@/types/block';
import type { ImageSuggestionTarget } from '@/types/image';
import type { Page } from '@/types/page';
import type { PageSection } from '@/types/page-section';

export interface ImageSuggestionsPageSummarySection {
  sectionTemplateCode: string;
  variant: string | null;
  textContent: string | null;
}

export interface ImageSuggestionsCardContext {
  textContent: string;
}

export interface ImageSuggestionsPageSummary {
  pageId: string;
  sections: ImageSuggestionsPageSummarySection[];
  card?: ImageSuggestionsCardContext;
}

const MAX_CONTEXT_LENGTH = 400;
const MAX_SECTIONS = 20;

type ChildrenByParent = Map<string, PageBlock[]>;

function joinContext(texts: string[]): string | null {
  const nonEmpty = texts.filter(Boolean);
  return nonEmpty.length > 0 ? nonEmpty.join(' ').slice(0, MAX_CONTEXT_LENGTH) : null;
}

function extractBlockTextContent(blocks: PageBlock[]): string | null {
  return joinContext(
    blocks.map((block) =>
      richTextToPlainText(
        normalizeRichTextContent((block.props as { content?: unknown } | undefined)?.content),
      ),
    ),
  );
}

// Collections and static data widgets both render editable item cards.
function readEditableCardItems(block: PageBlock): CollectionItem[] {
  if (block.blockType === BLOCK_TYPE.COLLECTION) return readCollectionItems(block.props?.items);
  if (!isDataWidgetBlock(block)) return [];

  // Static widgets keep their editable cards in staticItems, not items.
  const { source, items, staticItems } = resolveDataWidgetProps(block);
  return source.mode === 'static' ? staticItems : items;
}

function extractCollectionItemTextContent(
  blocks: PageBlock[],
  childrenByParent: ChildrenByParent,
  collectionBlockId: string,
  itemId: string,
): string | null {
  const collection = blocks.find((block) => block.id === collectionBlockId);
  const item = collection
    ? readEditableCardItems(collection).find((i) => i.id === itemId)
    : undefined;
  if (!collection || !item) return null;

  // Item values hold images, icons and buttons too, so only read the slots the
  // card template marks as text.
  const template = findCardTemplateBlock(childrenByParent.get(collection.id) ?? []);
  if (!template) return null;

  const textFields = getEnabledTextFields(
    resolveCardConfigFromTemplate(template, childrenByParent.get(template.id) ?? []).fields,
  );
  return joinContext(
    textFields.map((field) => richTextToPlainText(normalizeRichTextContent(item[field.slot]))),
  );
}

// Visual order (children by position, depth-first), so the card's leading text
// such as its title survives the context length cut.
function collectDescendantBlocks(childrenByParent: ChildrenByParent, rootId: string): PageBlock[] {
  return (childrenByParent.get(rootId) ?? []).flatMap((child) => [
    child,
    ...collectDescendantBlocks(childrenByParent, child.id),
  ]);
}

function extractGroupCardTextContent(
  blocks: PageBlock[],
  childrenByParent: ChildrenByParent,
  imageBlockId: string,
): string | null {
  const blocksById = new Map(blocks.map((block) => [block.id, block]));
  const parentGroup = (block: PageBlock | undefined) => {
    const parent = block?.parentBlockId ? blocksById.get(block.parentBlockId) : undefined;
    return parent?.blockType === BLOCK_TYPE.GROUP ? parent : undefined;
  };

  // Groups carry no "card" marker, and an image can sit in a text-less column
  // (card > media column > image). Walk up to the nearest group that holds text;
  // stopping there keeps sibling cards' text out.
  for (let group = parentGroup(blocksById.get(imageBlockId)); group; group = parentGroup(group)) {
    const textContent = extractBlockTextContent(
      collectDescendantBlocks(childrenByParent, group.id),
    );
    if (textContent) return textContent;
  }

  return null;
}

function extractCardContext(
  sections: PageSection[],
  target: ImageSuggestionTarget,
): ImageSuggestionsCardContext | undefined {
  const blocks = sections
    .map((section) => section.blocks || [])
    .find((sectionBlocks) => sectionBlocks.some((block) => block.id === target.blockId));
  if (!blocks) return undefined;

  const { childrenByParent } = buildBlockTree(blocks);
  const textContent =
    target.kind === 'collectionItem'
      ? extractCollectionItemTextContent(blocks, childrenByParent, target.blockId, target.itemId)
      : extractGroupCardTextContent(blocks, childrenByParent, target.blockId);
  return textContent ? { textContent } : undefined;
}

/**
 * Builds the context the image suggestions agent sees: the image's section (or every
 * section when it has none) plus, for card images, that card's own text.
 */
export function buildImageSuggestionsPageSummary(
  page: Page,
  sectionId?: string,
  target?: ImageSuggestionTarget,
): ImageSuggestionsPageSummary {
  const allSections = getSortedSections(getMainSections(page));
  const scopedSections = sectionId
    ? allSections.filter((section) => section.id === sectionId)
    : allSections;
  const sourceSections = scopedSections.length > 0 ? scopedSections : allSections;

  const sections = sourceSections.slice(0, MAX_SECTIONS).map((section) => ({
    sectionTemplateCode: section.sectionTemplateCode,
    variant: section.variant ?? null,
    textContent: extractBlockTextContent(section.blocks || []),
  }));

  // Without card text (no target, or an empty card) suggestions stay section-level.
  const card = target ? extractCardContext(sourceSections, target) : undefined;

  return card ? { pageId: page.id, sections, card } : { pageId: page.id, sections };
}
